/**
 * slotIcons.js — High-Plasticity 3D Military-Sci-Fi SVG Iconography
 * for Supply Surge Reels (Reel 1 Player Entry + 10 Reel 2 Categories)
 * and Arsenal Unit / Telemetry Tabs.
 *
 * DESIGN PARADIGMS (see docs/ARSENAL_SUPPLY_SURGE.md & CLAUDE.md):
 * 1. Zero Emojis, Zero Monochrome, Zero Synthwave Neon:
 *    Never use flat 2D wireframe lines or magenta/purple/cyan neon strokes.
 * 2. Grounded 3D Material Palette:
 *    Use layered polygons with shared metallic/enamel/reactor SVG gradients:
 *    - Brushed Titanium / Hull Steel (#metal-hull, #dark-steel)
 *    - Imperial Gold & Brass (#gold-trim, #brass-cylinder, #bronze-plate)
 *    - Crimson Enamel (#crimson-enamel) & Vital Emerald (#emerald-cell, #lime-cell)
 *    - Incandescent Reactor / Plasma Fire (#plasma-fire, #warm-amber-glow)
 * 3. Physical Depth & Specular Edges:
 *    Combine dark recessed wells (#090d16 / #050811), beveled outer chassis
 *    polygons, gold corner clamps, and crisp top/left specular highlights.
 * 4. Dual-Scale Optimization:
 *    - 'reel' (48-60px): Full 3D detail, recessed wells, specular lines.
 *    - 'mini' (13-16px): Simplified high-contrast silhouettes, bolder fins/strokes,
 *      and bright fills that never blend into dark button backgrounds.
 */

export const SLOT_ICON_DEFS_SVG = `<svg id="slot-icons-shared-defs" width="0" height="0" style="position:absolute;width:0;height:0;overflow:hidden;pointer-events:none" aria-hidden="true">
  <defs>
    <linearGradient id="metal-hull" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#f8fafc"/>
      <stop offset="45%" stop-color="#94a3b8"/>
      <stop offset="100%" stop-color="#334155"/>
    </linearGradient>
    <linearGradient id="gunmetal-hull" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#94a3b8"/>
      <stop offset="45%" stop-color="#475569"/>
      <stop offset="100%" stop-color="#1e293b"/>
    </linearGradient>
    <linearGradient id="silver-hull" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="38%" stop-color="#e2e8f0"/>
      <stop offset="78%" stop-color="#94a3b8"/>
      <stop offset="100%" stop-color="#475569"/>
    </linearGradient>
    <linearGradient id="dark-steel" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#475569"/>
      <stop offset="100%" stop-color="#0f172a"/>
    </linearGradient>
    <linearGradient id="gold-trim" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#fef08a"/>
      <stop offset="45%" stop-color="#f59e0b"/>
      <stop offset="100%" stop-color="#78350f"/>
    </linearGradient>
    <linearGradient id="diamond-gem" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="40%" stop-color="#e0f2fe"/>
      <stop offset="75%" stop-color="#7dd3fc"/>
      <stop offset="100%" stop-color="#0284c7"/>
    </linearGradient>
    <linearGradient id="brass-cylinder" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#fde047"/>
      <stop offset="35%" stop-color="#d97706"/>
      <stop offset="75%" stop-color="#92400e"/>
      <stop offset="100%" stop-color="#451a03"/>
    </linearGradient>
    <linearGradient id="bronze-plate" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#fdba74"/>
      <stop offset="50%" stop-color="#b45309"/>
      <stop offset="100%" stop-color="#78350f"/>
    </linearGradient>
    <linearGradient id="plasma-fire" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#dc2626"/>
      <stop offset="50%" stop-color="#f97316"/>
      <stop offset="85%" stop-color="#fde047"/>
      <stop offset="100%" stop-color="#ffffff"/>
    </linearGradient>
    <linearGradient id="warm-amber-glow" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#f59e0b" stop-opacity="0.75"/>
      <stop offset="55%" stop-color="#ea580c" stop-opacity="0.28"/>
      <stop offset="100%" stop-color="#ea580c" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="crimson-enamel" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#ef4444"/>
      <stop offset="55%" stop-color="#b91c1c"/>
      <stop offset="100%" stop-color="#450a0a"/>
    </linearGradient>
    <linearGradient id="sapphire-enamel" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#93c5fd"/>
      <stop offset="45%" stop-color="#2563eb"/>
      <stop offset="100%" stop-color="#172554"/>
    </linearGradient>
    <linearGradient id="cryo-plasma" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#1d4ed8"/>
      <stop offset="50%" stop-color="#0ea5e9"/>
      <stop offset="85%" stop-color="#bae6fd"/>
      <stop offset="100%" stop-color="#ffffff"/>
    </linearGradient>
    <linearGradient id="cryo-glow" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#0ea5e9" stop-opacity="0.75"/>
      <stop offset="55%" stop-color="#2563eb" stop-opacity="0.28"/>
      <stop offset="100%" stop-color="#1e3a8a" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="amethyst-enamel" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#e9d5ff"/>
      <stop offset="45%" stop-color="#9333ea"/>
      <stop offset="100%" stop-color="#3b0764"/>
    </linearGradient>
    <linearGradient id="emerald-plasma" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#065f46"/>
      <stop offset="45%" stop-color="#10b981"/>
      <stop offset="82%" stop-color="#6ee7b7"/>
      <stop offset="100%" stop-color="#ecfdf5"/>
    </linearGradient>
    <linearGradient id="emerald-cell" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#a7f3d0"/>
      <stop offset="30%" stop-color="#10b981"/>
      <stop offset="100%" stop-color="#064e3b"/>
    </linearGradient>
    <linearGradient id="lime-cell" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#d9f99d"/>
      <stop offset="30%" stop-color="#22c55e"/>
      <stop offset="100%" stop-color="#14532d"/>
    </linearGradient>
    <linearGradient id="cylinder-3d-shade" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#000000" stop-opacity="0.48"/>
      <stop offset="28%" stop-color="#ffffff" stop-opacity="0.32"/>
      <stop offset="65%" stop-color="#000000" stop-opacity="0.1"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0.55"/>
    </linearGradient>
    <linearGradient id="portrait-glass-sheen" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.28"/>
      <stop offset="35%" stop-color="#ffffff" stop-opacity="0.04"/>
      <stop offset="65%" stop-color="#000000" stop-opacity="0"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0.55"/>
    </linearGradient>
  </defs>
</svg>`;

/**
 * Ensures the shared 3D SVG gradient definitions exist in `document.body`
 * so any inline `<svg>` referencing `url(#metal-hull)` etc. renders properly.
 */
export function ensureSlotIconDefs() {
  if (typeof document === 'undefined' || !document.body) return;
  const existing = document.getElementById('slot-icons-shared-defs');
  if (existing) {
    if (
      existing.querySelector('#cylinder-3d-shade') &&
      existing.querySelector('#portrait-glass-sheen') &&
      existing.querySelector('#sapphire-enamel') &&
      existing.querySelector('#gunmetal-hull')
    ) {
      return;
    }
    existing.remove();
  }
  const wrapper = document.createElement('div');
  wrapper.innerHTML = SLOT_ICON_DEFS_SVG;
  const svgNode = wrapper.firstElementChild;
  if (svgNode) {
    document.body.appendChild(svgNode);
  }
}

export const SLOT_CATEGORY_ICONS = {
  // Reel 1 Global Player Entry: 3D Winged Imperial Command Shield & Star Crest
  player: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="1.5,9 10,11 9,17 3.5,15" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.8" stroke-linejoin="round"/>
      <polygon points="30.5,9 22,11 23,17 28.5,15" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.8" stroke-linejoin="round"/>
      <polygon points="4,15.5 10.5,17 9.5,21.5 5.5,20" fill="url(#bronze-plate)" stroke="#fbbf24" stroke-width="0.75" stroke-linejoin="round"/>
      <polygon points="28,15.5 21.5,17 22.5,21.5 26.5,20" fill="url(#bronze-plate)" stroke="#fbbf24" stroke-width="0.75" stroke-linejoin="round"/>
      <polygon points="16,2.5 25,6.5 23.5,20.5 16,29.5 8.5,20.5 7,6.5" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="1.15" stroke-linejoin="round"/>
      <polygon points="16,4.8 22.8,7.8 21.5,19.6 16,26.8 10.5,19.6 9.2,7.8" fill="url(#crimson-enamel)" stroke="url(#gold-trim)" stroke-width="0.95" stroke-linejoin="round"/>
      <polygon points="16,7.5 18.1,13.3 23.8,15.5 18.1,17.7 16,23.5 13.9,17.7 8.2,15.5 13.9,13.3" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.75" stroke-linejoin="round"/>
      <circle cx="16" cy="15.5" r="1.8" fill="#ffffff"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="16,2.5 25.5,6.5 23.5,20.5 16,29.5 8.5,20.5 6.5,6.5" fill="url(#crimson-enamel)" stroke="#fbbf24" stroke-width="2" stroke-linejoin="round"/>
      <polygon points="16,7 18.5,13 24.5,15.5 18.5,18 16,24 13.5,18 7.5,15.5 13.5,13" fill="#fef08a"/>
    </svg>`
  },

  // #01 · bullet_color: 3D Brass Cartridge + Titanium Sabot Collar + Molten Warhead + Muzzle Blast
  bullet_color: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M2.5 28.5L6.2 18.5L10.8 21.2L13.5 25.8L2.5 28.5Z" fill="url(#plasma-fire)" opacity="0.85"/>
      <path d="M3.8 27.2L7.5 21.2L11.2 24.5L3.8 27.2Z" fill="#fef08a"/>
      <polygon points="7.5,19.5 14.5,12.5 19.5,17.5 12.5,24.5" fill="url(#brass-cylinder)" stroke="#fde047" stroke-width="0.9" stroke-linejoin="round"/>
      <line x1="9" y1="19.5" x2="15" y2="13.5" stroke="#fef9c3" stroke-width="0.9" stroke-linecap="round" opacity="0.85"/>
      <polygon points="5.8,20.8 7.5,19.1 12.9,24.5 11.2,26.2" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.8" stroke-linejoin="round"/>
      <polygon points="14.5,12.5 17.5,9.5 22.5,14.5 19.5,17.5" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="0.9" stroke-linejoin="round"/>
      <path d="M17.5 9.5L23.5 4.2C26.2 2.2 29 3 29 3C29 3 29.8 5.8 27.8 8.5L22.5 14.5L17.5 9.5Z" fill="url(#crimson-enamel)" stroke="#fca5a5" stroke-width="0.95" stroke-linejoin="round"/>
      <path d="M22.5 5.2C25.2 3.2 28.8 3.2 28.8 3.2C28.8 3.2 28.8 6.8 26.8 9.5L22.5 5.2Z" fill="url(#plasma-fire)"/>
      <line x1="18.8" y1="13.2" x2="25.5" y2="6.5" stroke="#fef08a" stroke-width="1.1" stroke-linecap="round"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M2 29L6.5 18.5L13.5 25.5L2 29Z" fill="#f97316"/>
      <polygon points="7.5,19.5 15,12 20,17 12.5,24.5" fill="url(#brass-cylinder)" stroke="#fde047" stroke-width="1.5" stroke-linejoin="round"/>
      <polygon points="15,12 18,9 23,14 20,17" fill="#cbd5e1" stroke="#f8fafc" stroke-width="1.4"/>
      <path d="M18 9L24 3.8C26.5 2 29.2 2.8 29.2 2.8C29.2 2.8 30 5.5 28.2 8L23 14L18 9Z" fill="url(#plasma-fire)" stroke="#fef08a" stroke-width="1.2"/>
    </svg>`
  },

  // #02 · unit_aura: Heavy Silver/Steel Emitter Ring + Bio-Emerald / Jade Energy Corona (no fire colors)
  unit_aura: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <ellipse cx="16" cy="24" rx="13" ry="4.6" fill="#090d16" stroke="url(#silver-hull)" stroke-width="1.15"/>
      <ellipse cx="16" cy="24" rx="9.4" ry="2.8" fill="rgba(16, 185, 129, 0.32)" stroke="#34d399" stroke-width="0.9"/>
      <path d="M7 23C6 17 9 13 10.5 9C11 13 12.5 15 12.5 15C13 10 15 6 16 3C17.5 6.5 19 10 19.5 15C19.5 15 21 13 21.5 9C23 13 26 17 25 23H7Z" fill="url(#emerald-plasma)" stroke="#34d399" stroke-width="0.8" stroke-linejoin="round"/>
      <path d="M11.5 23.5C11.5 19 13.5 16 16 11C18.5 16 20.5 19 20.5 23.5H11.5Z" fill="#ecfdf5" opacity="0.92"/>
      <line x1="16" y1="6.5" x2="16" y2="22.5" stroke="#ffffff" stroke-width="0.75" stroke-linecap="round" opacity="0.75"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <ellipse cx="16" cy="24.5" rx="13" ry="4.5" fill="#0f172a" stroke="#e2e8f0" stroke-width="2.2"/>
      <path d="M7 23C6 17 9 12.5 10.5 8.5C11.5 13 13 14.5 13 14.5C13.5 9.5 15 5.5 16 2.5C17.5 6 19 9.5 19 14.5C19 14.5 20.5 13 21.5 8.5C23 12.5 26 17 25 23H7Z" fill="url(#emerald-plasma)"/>
      <path d="M12 23.5C12 19 14 16 16 11.5C18 16 20 19 20 23.5H12Z" fill="#ecfdf5"/>
    </svg>`
  },

  // #03 · rocket_trail: Silver-White Interceptor Rocket with Sapphire Nosecone/Fins, Thermal Plume & Smoke
  rocket_trail: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="6.5" cy="25.5" r="3.6" fill="#334155" stroke="#cbd5e1" stroke-width="0.85"/>
      <circle cx="12" cy="23.5" r="3.0" fill="#475569" stroke="#e2e8f0" stroke-width="0.8"/>
      <circle cx="8.5" cy="19.5" r="2.6" fill="#475569" stroke="#e2e8f0" stroke-width="0.8"/>
      <path d="M6.5 25.8C10.2 23.8 14 20.8 19 14" stroke="#dc2626" stroke-width="3.8" stroke-linecap="round"/>
      <path d="M8.2 24.3C11.5 22.3 15 19.3 19.5 13.5" stroke="#f97316" stroke-width="2.3" stroke-linecap="round"/>
      <path d="M11 22C14 20 16.5 17.5 20 13" stroke="#fef08a" stroke-width="1.1" stroke-linecap="round"/>
      <polygon points="17.2,11.2 13,11 16,7.5 19.2,9.8" fill="url(#sapphire-enamel)" stroke="#93c5fd" stroke-width="0.8" stroke-linejoin="round"/>
      <polygon points="20.8,14.8 21,19 24.5,16 22.2,12.8" fill="url(#sapphire-enamel)" stroke="#93c5fd" stroke-width="0.8" stroke-linejoin="round"/>
      <path d="M18 11L25.5 4.5C27.2 3.2 28.8 3.2 28.8 3.2C28.8 3.2 28.8 4.8 27.5 6.5L21 14L18 11Z" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="0.95" stroke-linejoin="round"/>
      <path d="M24.2 5.5L28.8 3.2L26.5 7.8L24.2 5.5Z" fill="url(#sapphire-enamel)" stroke="#bfdbfe" stroke-width="0.6"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="5.5" cy="26.5" r="4.2" fill="#cbd5e1"/>
      <circle cx="11.5" cy="24.5" r="3.6" fill="#94a3b8"/>
      <circle cx="7.5" cy="20.5" r="3.4" fill="#94a3b8"/>
      <polygon points="16.5,12.5 19.5,15.5 11,26 4,28 6,21" fill="#f97316"/>
      <polygon points="17,13 19,15 11.5,23.5 7.5,24.5 8.5,20.5" fill="#fef08a"/>
      <polygon points="15.5,11.5 11,11.5 14.5,7.5 18.5,10" fill="#3b82f6" stroke="#93c5fd" stroke-width="0.8"/>
      <polygon points="20.5,16.5 20.5,21 24.5,17.5 22,13.5" fill="#3b82f6" stroke="#93c5fd" stroke-width="0.8"/>
      <path d="M16 11L24.5 3.5C27 1.8 30 2 30 2C30 2 30.2 5 28.5 7.5L21 16L16 11Z" fill="#f8fafc" stroke="#0f172a" stroke-width="1.2" stroke-linejoin="round"/>
      <path d="M23.5 4.2L30 2L27.8 8.5L23.5 4.2Z" fill="#2563eb"/>
    </svg>`
  },

  // #04 · frame: Armored Platinum/Silver & Royal Sapphire Card Chassis with Sapphire Crown Gem (no gold)
  frame: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="9,3 23,3 26,6 26,26 23,29 9,29 6,26 6,6" fill="url(#dark-steel)" stroke="url(#gunmetal-hull)" stroke-width="1.05" stroke-linejoin="round"/>
      <rect x="9.2" y="6.8" width="13.6" height="18.4" rx="1" fill="#090d16" stroke="#3b82f6" stroke-width="0.9"/>
      <polyline points="10.8,13.5 10.8,8.4 16.5,8.4" stroke="#93c5fd" stroke-width="0.75" stroke-linecap="round" opacity="0.8"/>
      <path d="M5.8 10.2V5.8L8.8 2.8H12.2M26.2 10.2V5.8L23.2 2.8H19.8M5.8 21.8V26.2L8.8 29.2H12.2M26.2 21.8V26.2L23.2 29.2H19.8" stroke="url(#silver-hull)" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"/>
      <polygon points="16,1.2 18.6,4.0 16,6.8 13.4,4.0" fill="url(#sapphire-enamel)" stroke="#bfdbfe" stroke-width="0.8" stroke-linejoin="round"/>
      <polygon points="16,2.4 17.4,4.0 16,5.6 14.6,4.0" fill="#e0f2fe"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="9,3 23,3 26,6 26,26 23,29 9,29 6,26 6,6" fill="#0f172a" stroke="#3b82f6" stroke-width="2"/>
      <path d="M5.5 11V5.8L8.8 2.5H12.5M26.5 11V5.8L23.2 2.5H19.5M5.5 21V26.2L8.8 29.5H12.5M26.5 21V26.2L23.2 29.5H19.5" stroke="#f8fafc" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/>
      <polygon points="16,1 19,4.2 16,7.5 13,4.2" fill="#3b82f6" stroke="#bfdbfe" stroke-width="0.8"/>
    </svg>`
  },

  // #05 · hp_bar_color: 3D Silver/Steel Medical HP Gauge + Lush Emerald/Lime Cells + Ruby/Silver Vitality Shield
  hp_bar_color: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="11.2,2.5 20.8,2.5 23.2,5.8 16,13.2 8.8,5.8" fill="url(#crimson-enamel)" stroke="url(#silver-hull)" stroke-width="1.0" stroke-linejoin="round"/>
      <path d="M16 4.8V9.8M13.5 7.3H18.5" stroke="#ffffff" stroke-width="1.4" stroke-linecap="round"/>
      <polygon points="2,14.8 30,14.8 28.2,27.2 3.8,27.2" fill="url(#dark-steel)" stroke="url(#gunmetal-hull)" stroke-width="1.1" stroke-linejoin="round"/>
      <path d="M4.8 14.6L2,14.6L3.8,27.4L6.5,27.4M27.2,14.6L30,14.6L28.2,27.4L25.5,27.4" stroke="url(#silver-hull)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      <polygon points="4.8,16.8 27.2,16.8 25.8,25.2 6.2,25.2" fill="#050811"/>
      <polygon points="6.0,17.6 12.2,17.6 11.3,24.4 5.2,24.4" fill="url(#emerald-cell)"/>
      <polygon points="13.2,17.6 19.4,17.6 18.5,24.4 12.3,24.4" fill="url(#emerald-cell)"/>
      <polygon points="20.4,17.6 26.4,17.6 25.4,24.4 19.5,24.4" fill="url(#lime-cell)"/>
      <line x1="6.6" y1="18.6" x2="25.6" y2="18.6" stroke="#ffffff" stroke-width="0.8" stroke-linecap="round" opacity="0.75"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="10.5,2 21.5,2 24,6 16,14 8,6" fill="#dc2626" stroke="#f8fafc" stroke-width="1.8" stroke-linejoin="round"/>
      <path d="M16 4.5V10.5M13 7.5H19" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round"/>
      <polygon points="1.5,15 30.5,15 28.5,27.5 3.5,27.5" fill="#0f172a" stroke="#e2e8f0" stroke-width="2.2" stroke-linejoin="round"/>
      <polygon points="5.5,17.8 12,17.8 11,24.5 4.8,24.5" fill="#059669"/>
      <polygon points="13.2,17.8 19.5,17.8 18.5,24.5 12.2,24.5" fill="#10b981"/>
      <polygon points="20.8,17.8 26.5,17.8 25.5,24.5 19.8,24.5" fill="#4ade80"/>
    </svg>`
  },

  // #06 · avatar_border: Heavy Octagonal Royal Amethyst & Imperial Gold Medallion Ring
  avatar_border: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="11,3 21,3 29,11 29,21 21,29 11,29 3,21 3,11" fill="url(#amethyst-enamel)" stroke="url(#gold-trim)" stroke-width="1.15" stroke-linejoin="round"/>
      <polygon points="12.4,6.8 19.6,6.8 25.2,12.4 25.2,19.6 19.6,25.2 12.4,25.2 6.8,19.6 6.8,12.4" fill="#090d16" stroke="url(#silver-hull)" stroke-width="0.95" stroke-linejoin="round"/>
      <path d="M16 1.2V5.2M16 26.8V30.8M1.2 16H5.2M26.8 16H30.8" stroke="#fbbf24" stroke-width="1.35" stroke-linecap="round"/>
      <polygon points="16,11.5 20.5,16 16,20.5 11.5,16" fill="url(#amethyst-enamel)" stroke="#f3e8ff" stroke-width="0.8" stroke-linejoin="round"/>
      <polygon points="16,13.4 18.6,16 16,18.6 13.4,16" fill="#f3e8ff" opacity="0.85"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="11,3 21,3 29,11 29,21 21,29 11,29 3,21 3,11" fill="#581c87" stroke="#fbbf24" stroke-width="2.4" stroke-linejoin="round"/>
      <polygon points="12.5,7.5 19.5,7.5 24.5,12.5 24.5,19.5 19.5,24.5 12.5,24.5 7.5,19.5 7.5,12.5" fill="#090d16" stroke="#e9d5ff" stroke-width="1.8" stroke-linejoin="round"/>
      <path d="M16 1V5.5M16 26.5V31M1 16H5.5M26.5 16H31" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round"/>
    </svg>`
  },

  // #07 · tray_color: Armored Steel Bridge Console Dock with 3 Seated Hand Cards & Ion-Sapphire Backlight
  tray_color: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <ellipse cx="16" cy="19" rx="14" ry="9" fill="url(#cryo-glow)"/>
      <rect x="4.5" y="6.5" width="6.8" height="12.5" rx="1" transform="rotate(-6 4.5 6.5)" fill="#1e293b" stroke="#94a3b8" stroke-width="0.95"/>
      <rect x="12.6" y="4.5" width="6.8" height="13.5" rx="1" fill="url(#sapphire-enamel)" stroke="#bae6fd" stroke-width="1.05"/>
      <rect x="20.5" y="5.8" width="6.8" height="12.5" rx="1" transform="rotate(6 20.5 5.8)" fill="#1e293b" stroke="#94a3b8" stroke-width="0.95"/>
      <polygon points="1.8,17.2 30.2,17.2 27.8,26.8 4.2,26.8" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="1.1" stroke-linejoin="round"/>
      <rect x="6" y="20.6" width="20" height="2.6" rx="1.1" fill="url(#cryo-plasma)"/>
      <line x1="7.5" y1="21.5" x2="24.5" y2="21.5" stroke="#ffffff" stroke-width="0.7" stroke-linecap="round" opacity="0.85"/>
      <path d="M4.2 26.8H9.2M22.8 26.8H27.8" stroke="#7dd3fc" stroke-width="1.35" stroke-linecap="round"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="5" y="5.5" width="6.5" height="12" rx="1" fill="#1e293b" stroke="#94a3b8" stroke-width="1.8"/>
      <rect x="12.8" y="4" width="6.5" height="13" rx="1" fill="#2563eb" stroke="#bae6fd" stroke-width="1.8"/>
      <rect x="20.5" y="5.5" width="6.5" height="12" rx="1" fill="#1e293b" stroke="#94a3b8" stroke-width="1.8"/>
      <polygon points="1.5,17 30.5,17 28,27.5 4,27.5" fill="#0f172a" stroke="#e2e8f0" stroke-width="2.2" stroke-linejoin="round"/>
      <line x1="6.5" y1="22.2" x2="25.5" y2="22.2" stroke="#38bdf8" stroke-width="2.8" stroke-linecap="round"/>
    </svg>`
  },

  // #08 · deck_color: 3 Layered Multi-Color Card Slabs (Sapphire, Emerald & Crimson) with Diamond Crest
  deck_color: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="13.5" y="2.5" width="14" height="19" rx="1.8" fill="url(#sapphire-enamel)" stroke="#93c5fd" stroke-width="1.0"/>
      <rect x="9.2" y="6.2" width="14" height="19" rx="1.8" fill="url(#emerald-cell)" stroke="#6ee7b7" stroke-width="1.0"/>
      <rect x="5" y="10" width="14" height="19" rx="1.8" fill="url(#crimson-enamel)" stroke="url(#gold-trim)" stroke-width="1.15"/>
      <rect x="7.0" y="12.0" width="10.0" height="15.0" rx="1" stroke="rgba(254, 240, 138, 0.55)" stroke-width="0.7"/>
      <polygon points="12,15.2 15.6,19.5 12,23.8 8.4,19.5" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="0.75" stroke-linejoin="round"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="13.5" y="2.5" width="14" height="19" rx="2" fill="#1d4ed8" stroke="#93c5fd" stroke-width="2"/>
      <rect x="9.2" y="6.2" width="14" height="19" rx="2" fill="#059669" stroke="#6ee7b7" stroke-width="2"/>
      <rect x="5" y="10" width="14" height="19" rx="2" fill="#991b1b" stroke="#fbbf24" stroke-width="2.2"/>
      <polygon points="12,15 15.5,19.5 12,24 8.5,19.5" fill="#ffffff"/>
    </svg>`
  },

  // #09 · taunt: 3D Beveled Silver/Sapphire Tactical Vox-Hailer + 4-Color Spectrum Equalizer + Ion Shockwave
  taunt: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="2.5,5 21.5,5 24.5,8 24.5,20 14.5,20 7.5,27 7.5,20 2.5,20" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="1.0" stroke-linejoin="round"/>
      <path d="M2.5,10V5H8M19,5H21.5L24.5,8V11" stroke="url(#sapphire-enamel)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      <polygon points="4.8,7.3 20.6,7.3 22.2,8.9 22.2,17.7 4.8,17.7" fill="#090d16" stroke="#475569" stroke-width="0.85" stroke-linejoin="round"/>
      <rect x="7" y="11.5" width="2.5" height="4.2" rx="0.6" fill="url(#emerald-cell)"/>
      <rect x="10.8" y="8.8" width="2.5" height="6.9" rx="0.6" fill="url(#cryo-plasma)"/>
      <rect x="14.6" y="9.8" width="2.5" height="5.9" rx="0.6" fill="url(#amethyst-enamel)"/>
      <rect x="18.2" y="12" width="2.4" height="3.7" rx="0.6" fill="url(#gold-trim)"/>
      <polygon points="26.5,8.8 29.2,12.5 26.5,16.2 27.8,12.5" fill="url(#cryo-plasma)" stroke="#bae6fd" stroke-width="0.65" stroke-linejoin="round"/>
      <path d="M29 6.8L30.8 12.5L29 18.2" stroke="#38bdf8" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="2.5,5 22.5,5 25.5,8 25.5,20 14.5,20 7.5,27 7.5,20 2.5,20" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="1.8" stroke-linejoin="round"/>
      <rect x="5" y="7.5" width="18" height="10" rx="1" fill="#090d16"/>
      <rect x="7" y="11" width="3" height="5" fill="#10b981"/>
      <rect x="11.5" y="8.5" width="3" height="7.5" fill="#38bdf8"/>
      <rect x="16" y="10" width="3" height="6" fill="#a855f7"/>
      <path d="M28 7.5L30.5 12.5L28 17.5" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`
  },

  // #10 · profile_artwork: 3D Commander Bust in Royal Sapphire & Silver Armor in Bronze Portrait Plaque
  profile_artwork: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="5,2.5 27,2.5 29.5,5 29.5,27 27,29.5 5,29.5 2.5,27 2.5,5" fill="#090d16" stroke="url(#bronze-plate)" stroke-width="1.05" stroke-linejoin="round"/>
      <circle cx="16" cy="15" r="10.5" fill="url(#cryo-glow)"/>
      <path d="M2.5 8.8V5L5 2.5H8.8M29.5 8.8V5L27 2.5H23.2M2.5 23.2V27L5 29.5H8.8M29.5 23.2V27L27 29.5H23.2" stroke="url(#silver-hull)" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
      <polygon points="8,21 24,21 26.5,28.5 5.5,28.5" fill="url(#sapphire-enamel)" stroke="#93c5fd" stroke-width="0.9" stroke-linejoin="round"/>
      <polygon points="3.5,23 11,19.5 12.5,28.5 3.5,28.5" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="0.9" stroke-linejoin="round"/>
      <polygon points="28.5,23 21,19.5 19.5,28.5 28.5,28.5" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="0.9" stroke-linejoin="round"/>
      <polygon points="12,20.5 16,25.5 20,20.5 18,20 16,22.8 14,20" fill="url(#gold-trim)"/>
      <polygon points="12,5.5 20,5.5 22.5,9.5 21.5,16.5 16,19.8 10.5,16.5 9.5,9.5" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="1.0" stroke-linejoin="round"/>
      <polygon points="10.5,14 14.5,15.5 16,19.2 11,16.5" fill="url(#dark-steel)"/>
      <polygon points="21.5,14 17.5,15.5 16,19.2 21,16.5" fill="url(#dark-steel)"/>
      <polygon points="10.8,10.2 21.2,10.2 19.8,13.8 16,15 12.2,13.8" fill="url(#gold-trim)" stroke="#78350f" stroke-width="0.55"/>
      <line x1="12.2" y1="11.5" x2="19.8" y2="11.5" stroke="#fef08a" stroke-width="0.95" stroke-linecap="round"/>
      <polygon points="14.5,3.8 17.5,3.8 17,9.5 15,9.5" fill="url(#sapphire-enamel)" stroke="#bfdbfe" stroke-width="0.65"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M2.5 9V4.5L4.5 2.5H9M29.5 9V4.5L27.5 2.5H23" stroke="#e2e8f0" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
      <polygon points="4,22.5 11.5,19.5 20.5,19.5 28,22.5 29,29 3,29" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="1.6" stroke-linejoin="round"/>
      <polygon points="11,20.5 21,20.5 19,29 13,29" fill="#2563eb" stroke="#93c5fd" stroke-width="1.4"/>
      <polygon points="11.5,5.5 20.5,5.5 23,10 21.5,17 16,20 10.5,17 9,10" fill="url(#silver-hull)" stroke="#ffffff" stroke-width="1.8" stroke-linejoin="round"/>
      <polygon points="10.5,10.2 21.5,10.2 20,14.2 16,15.5 12,14.2" fill="#f59e0b"/>
      <rect x="14.5" y="3.5" width="3" height="6" rx="0.8" fill="#3b82f6"/>
    </svg>`
  },

  // #11 · deckbuilder_card: 3D High-Tech Glass Chassis with Chamfered Carbon Armor & Cyan Glass Specular
  deckbuilder_card: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="4,2 28,2 30.5,5 30.5,27 28,30 4,30 1.5,27 1.5,5" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="1.1" stroke-linejoin="round"/>
      <rect x="3.5" y="4" width="25" height="24" rx="2.5" fill="#090d16" stroke="url(#metal-hull)" stroke-width="0.75"/>
      <polygon points="4,2 8,2 4,6" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.5"/>
      <polygon points="28,2 24,2 28,6" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.5"/>
      <polygon points="4,30 8,30 4,26" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.5"/>
      <polygon points="28,30 24,30 28,26" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.5"/>
      <rect x="5.5" y="6" width="21" height="20" rx="2" fill="url(#sapphire-enamel)" opacity="0.45"/>
      <rect x="5.5" y="6" width="21" height="5.5" fill="#060a14" opacity="0.9"/>
      <rect x="11" y="7.5" width="10" height="3.2" rx="1.6" fill="#040914" stroke="#f43f5e" stroke-width="0.6"/>
      <circle cx="13" cy="9.1" r="0.8" fill="#f43f5e"/>
      <circle cx="16" cy="9.1" r="0.8" fill="#f43f5e"/>
      <circle cx="19" cy="9.1" r="0.8" fill="#f43f5e"/>
      <circle cx="23.8" cy="8.6" r="1.8" fill="#060a14" stroke="#00ff88" stroke-width="0.8"/>
      <polygon points="5.5,19 19,6 23.5,6 7,22" fill="#ffffff" opacity="0.4"/>
      <line x1="6" y1="20" x2="20" y2="6.5" stroke="#ffffff" stroke-width="0.9" stroke-linecap="round" opacity="0.85"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="3" y="2" width="26" height="28" rx="4" fill="url(#dark-steel)" stroke="#00f0ff" stroke-width="1.8"/>
      <rect x="5.5" y="5" width="21" height="22" rx="2.5" fill="#090d16"/>
      <rect x="10.5" y="7.5" width="11" height="4" rx="2" fill="#040914" stroke="#f43f5e" stroke-width="1"/>
      <circle cx="12.5" cy="9.5" r="1" fill="#f43f5e"/>
      <circle cx="16" cy="9.5" r="1" fill="#f43f5e"/>
      <circle cx="19.5" cy="9.5" r="1" fill="#f43f5e"/>
      <line x1="7" y1="23" x2="22" y2="8" stroke="#ffffff" stroke-width="1.5" opacity="0.8"/>
    </svg>`
  },

  // #12 · deck_icon: 3D Faction Crest Insignia Shield in Titan & Imperial Gold with Sapphire Core
  deck_icon: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="16,1.5 28.5,7 28.5,19 16,30.5 3.5,19 3.5,7" fill="url(#dark-steel)" stroke="url(#gold-trim)" stroke-width="1.2" stroke-linejoin="round"/>
      <polygon points="16,4 26,8.5 26,18 16,27.5 6,18 6,8.5" fill="#090d16"/>
      <polygon points="16,4 16,27.5 6,18 6,8.5" fill="url(#metal-hull)" opacity="0.35"/>
      <polygon points="16,4 26,8.5 26,18 16,27.5" fill="url(#dark-steel)" opacity="0.6"/>
      <polygon points="16,8 22,12 22,19 16,24 10,19 10,12" fill="url(#sapphire-enamel)" stroke="url(#gold-trim)" stroke-width="0.8"/>
      <circle cx="16" cy="15.5" r="4.5" fill="url(#cryo-glow)"/>
      <polygon points="16,10.5 17.5,14 21,15.5 17.5,17 16,20.5 14.5,17 11,15.5 14.5,14" fill="#ffffff"/>
      <circle cx="16" cy="15.5" r="1.5" fill="#38bdf8"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon points="16,2 29,7.5 29,20 16,30 3,20 3,7.5" fill="url(#dark-steel)" stroke="url(#gold-trim)" stroke-width="2" stroke-linejoin="round"/>
      <polygon points="16,6 25,10 25,18 16,25 7,18 7,10" fill="#090d16"/>
      <polygon points="16,8 21.5,11.5 21.5,17.5 16,22 10.5,17.5 10.5,11.5" fill="#2563eb" stroke="#93c5fd" stroke-width="1.2"/>
      <polygon points="16,10 17.8,13.5 21.5,15 17.8,16.5 16,20 14.2,16.5 10.5,15 14.2,13.5" fill="#ffffff"/>
    </svg>`
  },

  // #13 · card_theme: 3D In-Game Hand Card Chassis ("Cards") in Crystal Armor with Overdrive Glass Edge
  card_theme: {
    reel: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="4" y="2" width="24" height="28" rx="3.5" fill="url(#dark-steel)" stroke="#38bdf8" stroke-width="1.2"/>
      <rect x="5.5" y="3.5" width="21" height="25" rx="2.5" fill="#060911"/>
      <rect x="7" y="5" width="18" height="22" rx="1.5" fill="url(#sapphire-enamel)" opacity="0.3"/>
      <line x1="7" y1="9" x2="25" y2="9" stroke="rgba(255,255,255,0.4)" stroke-width="0.8"/>
      <circle cx="21.5" cy="23.5" r="3.2" fill="#060e1a" stroke="#00ff88" stroke-width="1"/>
      <text x="21.5" y="25" text-anchor="middle" font-size="3.8" font-weight="bold" fill="#00ff88" font-family="sans-serif">5</text>
      <polygon points="7,20 20,7 24,7 8,23" fill="#ffffff" opacity="0.35"/>
      <line x1="6.5" y1="21" x2="21.5" y2="6" stroke="#ffffff" stroke-width="1" stroke-linecap="round" opacity="0.9"/>
    </svg>`,
    mini: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="4" y="2" width="24" height="28" rx="4" fill="url(#dark-steel)" stroke="#38bdf8" stroke-width="1.8"/>
      <rect x="6.5" y="4.5" width="19" height="23" rx="2.5" fill="#060911"/>
      <circle cx="21" cy="23" r="3.5" fill="#060e1a" stroke="#00ff88" stroke-width="1.2"/>
      <line x1="7" y1="22" x2="22" y2="7" stroke="#ffffff" stroke-width="1.5" opacity="0.85"/>
    </svg>`
  }
};

/**
 * Returns the inline SVG string for a cosmetic category at 'reel' (48-60px) or 'mini' (13-16px) scale.
 */
export function getSlotCategorySvg(categoryId, size = 'reel') {
  const entry = SLOT_CATEGORY_ICONS[categoryId];
  if (!entry) return '';
  return size === 'mini' ? (entry.mini || entry.reel) : entry.reel;
}

/**
 * Returns the 3D Rarity-Forged Octagon Frame overlay SVG for Reel 1 unit portraits.
 * Supports single-color per rarity at half frame thickness:
 * - 'Common': Metal (Gunmetal)
 * - 'Uncommon': Silver
 * - 'Rare': Gold
 * - 'Epic': Gold with Diamond crown gem (no red)
 */
export function getUnitRarityFrameSvg(rarity = 'Common') {
  const r = String(rarity || 'Common').toLowerCase();
  let frameGrad = 'url(#gunmetal-hull)';
  let crestMarkup = '';

  if (r === 'uncommon') {
    frameGrad = 'url(#silver-hull)';
  } else if (r === 'rare') {
    frameGrad = 'url(#gold-trim)';
  } else if (r === 'epic') {
    frameGrad = 'url(#gold-trim)';
    crestMarkup = `
      <polygon points="32,0.5 36.2,4.7 32,8.9 27.8,4.7" fill="url(#diamond-gem)" stroke="url(#gold-trim)" stroke-width="1.1" stroke-linejoin="round"/>
      <polygon points="32,2.1 34.4,4.7 32,7.3 29.6,4.7" fill="#ffffff"/>
    `;
  }

  return `<svg class="reel-unit-frame-svg" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <polygon points="11.5,3.5 52.5,3.5 60.5,11.5 60.5,52.5 52.5,60.5 11.5,60.5 3.5,52.5 3.5,11.5" fill="url(#portrait-glass-sheen)"/>
    <polygon points="12,4.6 52,4.6 59.4,12 59.4,52 52,59.4 12,59.4 4.6,52 4.6,12" stroke="#050811" stroke-width="1.3" stroke-linejoin="round"/>
    <polygon points="11.5,3.2 52.5,3.2 60.8,11.5 60.8,52.5 52.5,60.8 11.5,60.8 3.2,52.5 3.2,11.5" stroke="${frameGrad}" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M3.1,16.5V11.3L11.3,3.1H16.5M47.5,3.1H52.7L60.9,11.3V16.5M60.9,47.5V52.7L52.7,60.9H47.5M16.5,60.9H11.3L3.1,52.7V47.5" stroke="${frameGrad}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>
    <polyline points="6.8,22 6.8,13 13,6.8 24,6.8" stroke="#ffffff" stroke-opacity="0.65" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/>
    ${crestMarkup}
  </svg>`;
}

/**
 * Returns the 3D category-specific Reactor Cylinder SVG for Reel 3 styles and Arsenal swatches.
 * Categories: 'Plasma', 'Metallic', 'Industrial', 'Elemental', 'Exotic'.
 */
export function getReel3CylinderSvg(hex = '#00F0FF', category = 'Plasma') {
  switch (category) {
    case 'Plasma':
      return `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <polygon points="4,6.5 28,6.5 28,25.5 4,25.5" fill="url(#dark-steel)" stroke="url(#metal-hull)" stroke-width="1.0" stroke-linejoin="round"/>
        <path d="M4.3 11H6.2M4.3 16H6.2M4.3 21H6.2M25.8 11H27.7M25.8 16H27.7M25.8 21H27.7" stroke="#cbd5e1" stroke-width="0.85" stroke-linecap="round"/>
        <rect x="6.4" y="6.5" width="19.2" height="19" fill="#050811"/>
        <rect x="7.8" y="7.2" width="16.4" height="17.6" rx="2" fill="${hex}"/>
        <rect x="7.8" y="7.2" width="16.4" height="17.6" rx="2" fill="url(#cylinder-3d-shade)"/>
        <line x1="16" y1="8.2" x2="16" y2="23.8" stroke="#ffffff" stroke-opacity="0.28" stroke-width="2.2" stroke-linecap="round"/>
        <line x1="11.2" y1="8.5" x2="11.2" y2="23.5" stroke="#ffffff" stroke-opacity="0.88" stroke-width="1.3" stroke-linecap="round"/>
        <line x1="21.5" y1="9" x2="21.5" y2="23" stroke="#ffffff" stroke-opacity="0.3" stroke-width="0.75" stroke-linecap="round"/>
        <polygon points="11.5,1.5 20.5,1.5 22,3.4 10,3.4" fill="url(#dark-steel)" stroke="#e2e8f0" stroke-width="0.75" stroke-linejoin="round"/>
        <polygon points="4.8,3.4 27.2,3.4 28.6,6.8 3.4,6.8" fill="url(#metal-hull)" stroke="#f8fafc" stroke-width="0.95" stroke-linejoin="round"/>
        <polygon points="3.4,25.2 28.6,25.2 27.2,28.6 4.8,28.6" fill="url(#metal-hull)" stroke="#f8fafc" stroke-width="0.95" stroke-linejoin="round"/>
        <polygon points="10,28.6 22,28.6 20.5,30.5 11.5,30.5" fill="url(#dark-steel)" stroke="#e2e8f0" stroke-width="0.75" stroke-linejoin="round"/>
      </svg>`;

    case 'Metallic':
      return `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <polygon points="4,6.5 28,6.5 28,25.5 4,25.5" fill="url(#dark-steel)" stroke="url(#metal-hull)" stroke-width="1.0"/>
        <rect x="6.4" y="6.5" width="19.2" height="19" fill="#050811"/>
        <rect x="7.8" y="7.2" width="16.4" height="17.6" rx="1.5" fill="${hex}"/>
        <rect x="7.8" y="7.2" width="16.4" height="17.6" rx="1.5" fill="url(#cylinder-3d-shade)"/>
        <polygon points="8,7.5 14.5,7.5 8,16" fill="#ffffff" fill-opacity="0.25"/>
        <line x1="11.2" y1="8.5" x2="11.2" y2="23.5" stroke="#ffffff" stroke-opacity="0.88" stroke-width="1.3" stroke-linecap="round"/>
        <line x1="21.5" y1="9" x2="21.5" y2="23" stroke="#ffffff" stroke-opacity="0.35" stroke-width="0.75" stroke-linecap="round"/>
        <polygon points="4.8,2.2 27.2,2.2 29,6.8 3,6.8" fill="url(#metal-hull)" stroke="#f8fafc" stroke-width="0.95" stroke-linejoin="round"/>
        <circle cx="9.5" cy="4.5" r="1.0" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.4"/>
        <circle cx="22.5" cy="4.5" r="1.0" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.4"/>
        <polygon points="3,25.2 29,25.2 27.2,29.8 4.8,29.8" fill="url(#metal-hull)" stroke="#f8fafc" stroke-width="0.95" stroke-linejoin="round"/>
        <circle cx="9.5" cy="27.5" r="1.0" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.4"/>
        <circle cx="22.5" cy="27.5" r="1.0" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.4"/>
      </svg>`;

    case 'Industrial':
      return `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <polygon points="4.2,6.5 27.8,6.5 27.8,25.5 4.2,25.5" fill="url(#dark-steel)" stroke="url(#bronze-plate)" stroke-width="1.0" stroke-linejoin="round"/>
        <path d="M4.2,11V6.8M4.2,25.2V21M27.8,11V6.8M27.8,25.2V21" stroke="#fdba74" stroke-width="1.15" stroke-linecap="round"/>
        <rect x="6.4" y="6.5" width="19.2" height="19" fill="#050811"/>
        <rect x="7.8" y="7.2" width="16.4" height="17.6" rx="1.5" fill="${hex}"/>
        <rect x="7.8" y="7.2" width="16.4" height="17.6" rx="1.5" fill="url(#cylinder-3d-shade)"/>
        <line x1="11.2" y1="8.5" x2="11.2" y2="23.5" stroke="#ffffff" stroke-opacity="0.86" stroke-width="1.3" stroke-linecap="round"/>
        <line x1="21.5" y1="9" x2="21.5" y2="23" stroke="#ffffff" stroke-opacity="0.3" stroke-width="0.75" stroke-linecap="round"/>
        <polygon points="5.2,2.4 26.8,2.4 28.6,6.8 3.4,6.8" fill="url(#bronze-plate)" stroke="#fde047" stroke-width="0.9" stroke-linejoin="round"/>
        <path d="M12.5 4.6H19.5" stroke="#090d16" stroke-width="1.2" stroke-linecap="round"/>
        <polygon points="3.4,25.2 28.6,25.2 26.8,29.6 5.2,29.6" fill="url(#bronze-plate)" stroke="#fde047" stroke-width="0.9" stroke-linejoin="round"/>
        <path d="M12.5 27.4H19.5" stroke="#090d16" stroke-width="1.2" stroke-linecap="round"/>
      </svg>`;

    case 'Elemental':
      return `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="5" y="7" width="22" height="18.5" rx="2" fill="url(#dark-steel)" stroke="url(#metal-hull)" stroke-width="0.95"/>
        <rect x="6.5" y="7.2" width="19" height="18" fill="#050811"/>
        <rect x="8" y="7.8" width="16" height="16.8" rx="2.5" fill="${hex}"/>
        <rect x="8" y="7.8" width="16" height="16.8" rx="2.5" fill="url(#cylinder-3d-shade)"/>
        <line x1="11.2" y1="9.2" x2="11.2" y2="23.2" stroke="#ffffff" stroke-opacity="0.86" stroke-width="1.3" stroke-linecap="round"/>
        <path d="M21.2 11H23.4M21.8 14.2H23.4M20.8 17.4H23.4M21.8 20.6H23.4" stroke="#fef08a" stroke-width="0.85" stroke-linecap="round"/>
        <polygon points="11.8,1.4 20.2,1.4 18.8,3.4 13.2,3.4" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.7"/>
        <path d="M4.8 7.4C4.8 4.3 8.6 3.4 16 3.4C23.4 3.4 27.2 4.3 27.2 7.4H4.8Z" fill="url(#metal-hull)" stroke="url(#gold-trim)" stroke-width="0.95" stroke-linejoin="round"/>
        <polygon points="3.8,24.8 28.2,24.8 26.4,29.6 5.6,29.6" fill="url(#metal-hull)" stroke="url(#gold-trim)" stroke-width="0.95" stroke-linejoin="round"/>
      </svg>`;

    case 'Exotic':
    default:
      return `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <polygon points="2.2,10.5 6.5,8.5 6.5,23.5 2.2,21.5 3.8,16" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.75" stroke-linejoin="round"/>
        <polygon points="29.8,10.5 25.5,8.5 25.5,23.5 29.8,21.5 28.2,16" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.75" stroke-linejoin="round"/>
        <rect x="6" y="6.2" width="20" height="19.6" fill="#050811"/>
        <rect x="7.5" y="7" width="17" height="18" rx="2" fill="${hex}"/>
        <rect x="7.5" y="7" width="17" height="18" rx="2" fill="url(#cylinder-3d-shade)"/>
        <polygon points="16,9.5 20.5,16 16,22.5 11.5,16" stroke="#ffffff" stroke-opacity="0.45" stroke-width="0.75" fill="#ffffff" fill-opacity="0.12"/>
        <line x1="10.8" y1="8.8" x2="10.8" y2="23.2" stroke="#ffffff" stroke-opacity="0.88" stroke-width="1.3" stroke-linecap="round"/>
        <polygon points="4.2,2.4 27.8,2.4 26.4,6.8 18.5,6.8 16,9.0 13.5,6.8 5.6,6.8" fill="url(#crimson-enamel)" stroke="url(#gold-trim)" stroke-width="1.0" stroke-linejoin="round"/>
        <polygon points="5.6,25.2 13.5,25.2 16,23.0 18.5,25.2 26.4,25.2 27.8,29.6 4.2,29.6" fill="url(#crimson-enamel)" stroke="url(#gold-trim)" stroke-width="1.0" stroke-linejoin="round"/>
      </svg>`;
  }
}

